import { Alert, Box, Fab, FabProps, Snackbar } from '@mui/material';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { RiAlertFill, RiGiftLine } from 'react-icons/ri';

let hasAnimatedOnce = false;

export interface FloatingHelpButtonProps extends Omit<FabProps, 'children' | 'color' | 'size' | 'sx'> {
  offsetBottom?: number;
  hasNotification?: boolean;
  hasAlert?: boolean;
  alertBody?: string;
}
export const FloatingHelpButton = forwardRef<HTMLButtonElement, FloatingHelpButtonProps>((props, ref) => {
  // eslint-disable-next-line react/prop-types
  const { offsetBottom = 0, hasNotification, hasAlert, alertBody, onClick, ...fabProps } = props;
  const [showAlert, setShowAlert] = useState(false);

  const [showIcon, setShowIcon] = useState(hasAnimatedOnce);
  const [enlargeFab, setEnlargeFab] = useState(false);

  const queAnimation = useCallback(() => {
    const enlargeFabTimeoutId = setTimeout(() => {
      setEnlargeFab(true);
    }, 1000);
    const showIconTimeoutId = setTimeout(() => {
      setShowIcon(true);
    }, 1400);
    return () => {
      clearTimeout(enlargeFabTimeoutId);
      clearTimeout(showIconTimeoutId);
    };
  }, []);

  const triggerAnimationEndState = useCallback(() => {
    setShowIcon(true);
    setEnlargeFab(false);
  }, []);

  const shrinkFab = useCallback(() => {
    hasAnimatedOnce = true;
    setEnlargeFab(false);
  }, []);

  useEffect(() => {
    if (hasNotification || hasAlert) {
      if (!hasAnimatedOnce) {
        return queAnimation();
      } else {
        triggerAnimationEndState();
      }
    }
  }, [hasAlert, hasNotification, queAnimation, triggerAnimationEndState]);

  useEffect(() => {
    if ((hasNotification || hasAlert) && showIcon) {
      const shrinkFabTimeoutId = setTimeout(() => {
        shrinkFab();
      }, 1000);
      return () => {
        clearTimeout(shrinkFabTimeoutId);
      };
    }
  }, [hasAlert, hasNotification, showIcon, shrinkFab]);

  const backgroundColor = useMemo(() => {
    if (showIcon && (hasNotification || hasAlert)) {
      return hasAlert ? 'warning.dark' : 'accent.main';
    }
    return 'primary.main';
  }, [showIcon, hasNotification, hasAlert]);

  const hoverColor = useMemo(() => {
    if (showIcon && (hasNotification || hasAlert)) {
      return hasAlert ? 'warning.main' : 'accent.light';
    }
    return 'primary.dark';
  }, [showIcon, hasNotification, hasAlert]);

  const handleClosePopover = useCallback(() => {
    setShowAlert(false);
  }, []);

  const handleFabClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setShowAlert(false);
      onClick && onClick(event);
    },
    [onClick]
  );

  useEffect(() => {
    if (hasAlert && !hasAnimatedOnce) {
      setShowAlert(true);
    }
  }, [hasAlert]);

  //   //DEBUG: after 5 seconds, reset showIcon to false, que animation again
  //   useEffect(() => {
  //     let timeoutId: NodeJS.Timeout;
  //     if (hasNotification && showIcon) {
  //       timeoutId = setTimeout(() => {
  //         setShowIcon(false);
  //         queAnimation();
  //       }, 5000);
  //     }
  //     return () => clearTimeout(timeoutId);
  //   }, [hasNotification, queAnimation, showIcon]);

  return (
    <>
      <Fab
        {...fabProps}
        onClick={handleFabClick}
        ref={ref}
        disabled={false}
        disableFocusRipple={true}
        sx={{
          position: 'fixed',
          bottom: 24 + offsetBottom,
          fontSize: 24,
          fontWeight: 500,
          height: 56,
          left: 24,
          width: 56,
          backgroundColor: backgroundColor,
          color: 'white',
          overflow: 'clip',
          '&:hover': {
            backgroundColor: hoverColor,
          },
          '&:focus': {
            backgroundColor: hoverColor,
          },
          //   scale up FAB   right before animation starts & scale down one second after animation ends
          ...((hasNotification || hasAlert) && {
            transition: 'transform .2s ease-in-out',

            transform: enlargeFab ? 'scale(1.2)' : 'scale(1)',
          }),
        }}
      >
        {hasNotification && !hasAlert && (
          <Box
            sx={{
              position: 'absolute',
              transition: 'transform .5s ease-in-out',
              transform: showIcon ? 'translateY(0)' : 'translateY(56px)',
            }}
          >
            <RiGiftLine />
          </Box>
        )}
        {hasAlert && (
          <Box
            sx={{
              position: 'absolute',
              transition: 'transform .5s ease-in-out',
              transform: showIcon ? 'translateY(0)' : 'translateY(56px)',
            }}
          >
            <RiAlertFill />
          </Box>
        )}
        <Box
          sx={{
            position: 'absolute',
            ...((hasNotification || hasAlert) && {
              transition: 'transform .5s ease-in-out',
              transform: showIcon ? 'translateY(-56px)' : 'translateY(2px)',
            }),
          }}
        >
          ?
        </Box>
      </Fab>
      {!!hasAlert && showAlert && hasAnimatedOnce && (
        <Snackbar
          open={true}
          onClose={handleClosePopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          sx={{
            bottom: { xs: 60 + offsetBottom, sm: 20 + offsetBottom },
            left: { sm: 84 },
            maxWidth: '500px',
          }}
        >
          <Alert icon={false} onClose={handleClosePopover} variant='filled' severity='warning' sx={{ mb: 3 }}>
            {alertBody}
          </Alert>
        </Snackbar>
      )}
    </>
  );
});

FloatingHelpButton.displayName = 'FloatingHelpButton';
